import { memo, useRef } from "react";
import { motion, useReducedMotion, useAnimation } from "framer-motion";
import { ContentCardProps } from "./LargeCard";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";
import CardHeading from "./CardHeading";
import { useAudioEffects } from "../../../../../../hooks/use-audio-effects";
import openSound from "../../../../assets/audio/menu-open.wav";

const SmallCard: React.FC<ContentCardProps> = ({
  item,
  onClick,
  className = "",
}) => {
  const rm = useReducedMotion();
  const { playEffect } = useAudioEffects();
  const ref = useRef<HTMLButtonElement>(null);

  // Icon parallax (mouseX/Y → useSpring → iconX/Y) removed — the springs fired
  // a recalc on every mousemove frame, adding JS work on every pixel of movement.
  // The parent wrapper already provides scale + brightness on hover.

  const shimmerControls = useAnimation();

  const handleMouseEnter = () => {
    if (rm) return;
    shimmerControls.set({ x: "-140%" });
    shimmerControls.start({ x: "300%", transition: { duration: 0.5, ease: "easeInOut" } });
  };

  const handleMouseLeave = () => {
    shimmerControls.stop();
    shimmerControls.set({ x: "-140%" });
  };

  return (
    <BackgroundImage
      imageSrc={item.backgroundImageSrc + ""}
      imageClassName={`rounded-xl ${item.objectFitCover ? "!object-cover" : "!object-fill"} opacity-70`}
      className="w-full h-full overflow-hidden"
    >
      <button
        ref={ref}
        onClick={() => {
          playEffect(openSound);
          onClick();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={[
          "overflow-hidden w-full h-full p-8",
          "bg-transparent",
          "chakra-petch-font",
          className,
        ].join(" ")}
      >
        <div className="w-full h-full relative">
          <div>
            <CardHeading
              label={item.label}
              description={item.description}
              ctaText={item.ctaText}
              onCtaClick={onClick}
            />
          </div>

          <div className="absolute top-0 left-0 right-0 flex items-center justify-center bottom-0">
            <img
              className="relative bottom-[-30px] w-full max-w-[100px] 1600px:max-w-[160px] 2560px:max-w-[250px]"
              src={item.iconSrc}
              alt="icon"
              draggable={false}
              width={160}
              height={200}
            />
          </div>
        </div>
      </button>

      {!rm && (
        <motion.div
          aria-hidden
          animate={shimmerControls}
          initial={{ x: "-140%" }}
          className="absolute inset-y-0 w-[50%] bg-gradient-to-r from-transparent via-white/[0.07] to-transparent -skew-x-[18deg] pointer-events-none"
        />
      )}
    </BackgroundImage>
  );
};

export default memo(SmallCard);
